import { useEffect, useRef } from 'react';
import { Button, Card, Space, Typography, ConfigProvider } from 'antd';
import { HomeOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { isRouteErrorResponse, useNavigate, useRouteError, useLocation } from 'react-router-dom';
import { lightTheme } from '@/styles/theme';

const { Title, Text, Paragraph } = Typography;

function resolveErrorMessage(error: unknown): { title: string; description: string } {
  if (isRouteErrorResponse(error)) {
    const status = error.status || 500;
    const statusText = error.statusText || 'Erro inesperado';
    return {
      title: `${status} • ${statusText}`,
      description: error.data?.message || 'Algo não saiu como esperado durante o carregamento da página.',
    };
  }

  if (error instanceof Error) {
    return {
      title: 'Ocorreu um erro inesperado',
      description: error.message || 'Tivemos um problema ao processar sua solicitação.',
    };
  }

  return {
    title: 'Oops! Tivemos um imprevisto',
    description: 'Não conseguimos exibir esta página agora. Tente novamente em instantes.',
  };
}

export default function RouteErrorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const error = useRouteError();
  const prevThemeRef = useRef<string | null>(null);
  const { title, description } = resolveErrorMessage(error);

  // Detectar se está em contexto público
  const publicPaths = ['/', '/home-public', '/faq', '/privacidade', '/termos', '/cookies', '/signup', '/login'];
  const isPublicPage = publicPaths.some(path => location.pathname === path || location.pathname.startsWith(path + '/'));

  // Forçar light mode em contexto público
  useEffect(() => {
    if (isPublicPage) {
      try {
        prevThemeRef.current = document.documentElement.getAttribute('data-theme');
        document.documentElement.setAttribute('data-theme', 'light');
      } catch {}
    }
    return () => {
      try {
        if (isPublicPage && prevThemeRef.current) {
          document.documentElement.setAttribute('data-theme', prevThemeRef.current);
        }
      } catch {}
    };
  }, [isPublicPage]);

  const handleHomeClick = () => {
    // Se está em contexto público, vai para a homepage pública
    if (isPublicPage) {
      navigate('/');
    } else {
      // Se está na área logada, vai para a home da área logada
      navigate('/home');
    }
  };

  return (
    <ConfigProvider theme={isPublicPage ? lightTheme : undefined}>
      <div className="error-page" role="alert" style={isPublicPage ? { background: '#f5f7fb', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' } : undefined}>
  <Card className="error-page__card" variant="borderless">
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Title level={2} className="error-page__title">
              Algo saiu do roteiro
            </Title>
            <Paragraph className="error-page__description">
              {description}
            </Paragraph>
            <Text type="secondary">{title}</Text>
          </Space>

          <Space size={[12, 12]} wrap>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
            >
              Recarregar página
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            >
              Voltar
            </Button>
            <Button
              icon={<HomeOutlined />}
              onClick={handleHomeClick}
            >
              Ir para a Home
            </Button>
          </Space>

          <Paragraph className="error-page__footnote">
            Se o problema persistir, contate a equipe de suporte informando o horário e a ação realizada.
          </Paragraph>
        </Space>
      </Card>
    </div>
    </ConfigProvider>
  );
}
